import { inject, Injectable } from '@angular/core'

import { tap } from 'rxjs'

import { AgentEvent } from '@yoshu-lib/models/agent-event.model'
import { ChatEventType } from '@yoshu-lib/models/chat-event.model'
import { NewMessage } from '@yoshu-lib/models/chat-message.model'

import { ProtocolStepPayload } from '@yoshu-lib/core/api/protocol-sessions/protocol-sessions.model'
import { SocketService } from '@yoshu-lib/core/socket/socket.service'

export const PROTOCOL_EVENTS = {
  STEP: 'protocol.step',
  END: 'protocol.end',
  ERROR: 'protocol.error',
  SESSION: 'protocol.session',
  START: 'protocol.start',
  TRANSITION: 'protocol.transition',
  BACK: 'protocol.back',
  OPEN_HELPER: 'protocol.open_helper',
  HELPER_OPENED: 'protocol.helper_opened'
} as const

export interface ProtocolSessionCreatedEvent {
  session_id: string
  protocol_id: string
  protocol_slug: string
  protocol_name: string
}

export interface ProtocolHelperOpenedEvent {
  helper_id: string
  chat_id: string
}

@Injectable({ providedIn: 'root' })
export class StepModeGatewayService {
  private readonly socket = inject(SocketService)
  private readonly sessionNameCache = new Map<string, string>()

  listenStep() {
    return this.socket.listen<ProtocolStepPayload>(PROTOCOL_EVENTS.STEP, 'protocol')
  }

  listenEnd() {
    return this.socket.listen<{ status: string }>(PROTOCOL_EVENTS.END, 'protocol')
  }

  listenError() {
    return this.socket.listen<{ message: string }>(PROTOCOL_EVENTS.ERROR, 'protocol')
  }

  listenChatMessages() {
    return this.socket.listen<NewMessage>(ChatEventType.NewMessage, 'chat')
  }

  listenChatAgentEvents() {
    return this.socket.listen<AgentEvent>(ChatEventType.AgentEvent, 'chat')
  }

  joinChatRoom(chatId: string) {
    this.socket.emit(ChatEventType.JoinChatRooms, { chat_id: chatId })
  }

  sendChatMessage(payload: {
    chat_id: string
    message: string
    workflow: string
    images?: unknown[]
  }) {
    this.socket.emit(ChatEventType.SendMessage, payload, 'chat')
  }

  listenSessionCreated() {
    return this.socket
      .listen<ProtocolSessionCreatedEvent>(PROTOCOL_EVENTS.SESSION, 'protocol')
      .pipe(
        tap((event) => {
          if (!event?.session_id) {
            return
          }

          const name = typeof event.protocol_name === 'string' ? event.protocol_name.trim() : ''
          if (name) {
            this.sessionNameCache.set(event.session_id, name)
          }
        })
      )
  }

  getCachedSessionName(sessionId: string) {
    return this.sessionNameCache.get(sessionId) ?? null
  }

  listenHelperOpened() {
    return this.socket.listen<ProtocolHelperOpenedEvent>(PROTOCOL_EVENTS.HELPER_OPENED, 'protocol')
  }

  resumeSession(sessionId: string) {
    this.socket.emit(
      PROTOCOL_EVENTS.START,
      {
        session_id: sessionId
      },
      'protocol'
    )
  }

  startNewSession(protocolId: string) {
    this.socket.emit(
      PROTOCOL_EVENTS.START,
      {
        protocol_id: protocolId
      },
      'protocol'
    )
  }

  transition(trigger: string, value?: unknown) {
    const payload: Record<string, unknown> = { trigger }
    if (typeof value !== 'undefined') {
      payload['value'] = value
    }
    this.socket.emit(PROTOCOL_EVENTS.TRANSITION, payload, 'protocol')
  }

  openHelper(helperId: string) {
    this.socket.emit(PROTOCOL_EVENTS.OPEN_HELPER, { helper_id: helperId }, 'protocol')
  }

  goBack() {
    this.socket.emit(PROTOCOL_EVENTS.BACK, {}, 'protocol')
  }
}
